import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useLanguage } from '../../i18n/index.jsx'

interface LanguageOption {
  code: string
  short: string
}

interface SearchResult {
  name: string
  type: string
  [key: string]: unknown
}

interface MobileTopbarProps {
  mobileSearchOpen: boolean
  setMobileSearchOpen: (open: boolean) => void
  searchQuery: string
  onSearch: (query: string) => void
  searchResults: SearchResult[]
  showSearchDropdown: boolean
  searchHighlight: number
  setSearchHighlight: (highlight: number | ((h: number) => number)) => void
  onSearchSelect: (result: SearchResult) => void
  logoUrl: string | null | undefined
  appName: string | null | undefined
  theme?: string
  toggleTheme: () => void
  getThemeEmoji: () => string
  lang: string
  setLang: (lang: string) => void
  languageOptions: LanguageOption[]
  menuTriggerRef: React.RefObject<HTMLButtonElement | null>
  menuOpen: boolean
  setMenuOpen: (open: boolean) => void
}

export default function MobileTopbar({
  mobileSearchOpen, setMobileSearchOpen,
  searchQuery, onSearch, searchResults, showSearchDropdown,
  searchHighlight, setSearchHighlight, onSearchSelect,
  logoUrl, appName,
  theme: _theme, toggleTheme, getThemeEmoji,
  lang, setLang, languageOptions,
  menuTriggerRef, menuOpen, setMenuOpen,
}: MobileTopbarProps) {
  const navigate = useNavigate()
  const { t } = useLanguage()

  return (
    <header className="mobile-topbar">
      {mobileSearchOpen ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <input
            type="text"
            placeholder={t('filterSearch')}
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { onSearch(e.target.value); setSearchHighlight(-1) }}
            onFocus={() => searchQuery && showSearchDropdown}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (!showSearchDropdown || !searchResults.length) {
                if (e.key === 'Escape') { setMobileSearchOpen(false); onSearch('') }
                return
              }
              if (e.key === 'ArrowDown') { e.preventDefault(); setSearchHighlight(h => (h + 1) % searchResults.length) }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setSearchHighlight(h => (h <= 0 ? searchResults.length - 1 : h - 1)) }
              else if (e.key === 'Enter' && searchHighlight >= 0) { e.preventDefault(); onSearchSelect(searchResults[searchHighlight]); setMobileSearchOpen(false); onSearch('') }
              else if (e.key === 'Escape') { setSearchHighlight(-1) }
            }}
            autoFocus
            aria-label={t('search')}
            role="combobox"
            aria-expanded={showSearchDropdown && searchResults.length > 0}
            aria-autocomplete="list"
            aria-controls="mobile-search-listbox"
            aria-activedescendant={searchHighlight >= 0 ? `mobile-search-option-${searchHighlight}` : undefined}
            style={{
              flex: 1, padding: '6px 10px', fontSize: 'var(--text-base)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius)', color: 'var(--text-primary)',
            }}
          />
          <button
            onClick={() => { setMobileSearchOpen(false); onSearch('') }}
            aria-label={t('close')}
            className="topbar-icon-btn"
          >✕</button>
          {showSearchDropdown && searchResults.length > 0 && (
            <div
              id="mobile-search-listbox"
              role="listbox"
              style={{
                position: 'absolute', top: '100%', left: 8, right: 8,
                background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)',
                maxHeight: 300, overflowY: 'auto', zIndex: 'var(--z-dropdown)',
              }}
            >
              {searchResults.map((r, i) => (
                <div
                  key={i}
                  id={`mobile-search-option-${i}`}
                  role="option"
                  aria-selected={i === searchHighlight}
                  onClick={() => { onSearchSelect(r); setMobileSearchOpen(false); onSearch('') }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSearchSelect(r); setMobileSearchOpen(false); onSearch('') } }}
                  onMouseEnter={() => setSearchHighlight(i)}
                  style={{
                    padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)', fontSize: 'var(--text-caption)',
                    background: i === searchHighlight ? 'var(--bg-elevated)' : 'transparent',
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{r.name}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{r.type}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <button type="button" onClick={() => navigate('/')}
            aria-label={`${appName || 'Brighella'} home`}
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', textDecoration: 'none', color: 'inherit', background: 'transparent', border: 'none', padding: 0, font: 'inherit' }}>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" style={{ height: 24, width: 'auto', maxWidth: 100, objectFit: 'contain' }} onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.onerror = null; e.currentTarget.src = '/logo.png' }} loading="eager" />
            ) : (
              <img src="/logo.png" alt="Brighella" className="brand-icon-sm" style={{ width: 24, height: 24, objectFit: 'contain' }} loading="eager" width={24} height={24} />
            )}
            <span className="brand-name-sm">{appName || 'Brighella'}</span>
          </button>
          <button
            onClick={toggleTheme}
            className="topbar-icon-btn"
            style={{ marginLeft: 'auto' }}
          >{getThemeEmoji()}</button>
          <select
            value={lang}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLang(e.target.value)}
            aria-label={t('changeLanguage')}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer', padding: '4px 2px' }}
          >
            {languageOptions.map(o => (
              <option key={o.code} value={o.code}>{o.short}</option>
            ))}
          </select>
          <button
            onClick={() => setMobileSearchOpen(true)}
            className="topbar-icon-btn"
            aria-label={t('search')}
          ><Search size={18} /></button>
          <button ref={menuTriggerRef} className="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-expanded={menuOpen} aria-label={t('ariaOpenMenu')}>☰</button>
        </>
      )}
    </header>
  )
}
